import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { AccordionGroup } from '@startuptools/ui/AccordionGroup';
import { Accordion } from '@startuptools/ui/Accordion';
import { AccordionSummary } from '@startuptools/ui/AccordionSummary';
import { AccordionDetails } from '@startuptools/ui/AccordionDetails';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Card } from '@startuptools/ui/Card';
import { Chip } from '@startuptools/ui/Chip';
import { useState } from 'react';
import {
  BoardMeetingTemplatesDocument,
  useBoardMeetingTemplatesSuspenseQuery,
  useCreateMeetingAgendaTemplateMutation,
} from './BoardMeetingTemplates.graphql';
import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { BoardMeetingTemplateDetails } from './BoardMeetingTemplateDetails';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PromptDialog } from '../../components/simple-dialogs/PromptDialog';
import { Checkbox } from '@startuptools/ui/Checkbox';

type FormValues = {
  name: string;
  isDefault: boolean;
};

export const BoardMeetingTemplates = () => {
  const companyId = useCompanyId();
  const [openedMat, setOpenedMat] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const {
    data: { meetingAgendaTemplates },
  } = useBoardMeetingTemplatesSuspenseQuery({ variables: { companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateMat] = useCreateMeetingAgendaTemplateMutation({ refetchQueries: [BoardMeetingTemplatesDocument] });

  const { register, handleSubmit } = useForm<FormValues>({ defaultValues: { name: '', isDefault: false } });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    await doCreateMat({ variables: { companyId, input: values } });
    setShowAddDialog(false);
    return;
  };

  return (
    <>
      <Card>
        <Stack gap={3}>
          <Typography level="h2">
            <Trans id="Board Meeting Templates" />
          </Typography>

          <Box>
            <Button onClick={() => setShowAddDialog(true)}>
              <Trans id="Create Template" />
            </Button>
          </Box>

          <AccordionGroup variant="outlined" size="lg">
            {meetingAgendaTemplates.map(mat => (
              <Accordion
                key={mat.id}
                accordionId={mat.id}
                expanded={openedMat === mat.id}
                onChange={(_, expanded) => setOpenedMat(expanded ? mat.id : null)}
              >
                <AccordionSummary>
                  <Stack direction="row" alignItems="center" gap={1}>
                    {mat.name}{' '}
                    {mat.isDefault && (
                      <Chip variant="outlined" size="sm" color="primary">
                        <Trans id="default" />
                      </Chip>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={theme => ({ backgroundColor: theme.palette.background.body })}>
                  <BoardMeetingTemplateDetails mat={mat} />
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionGroup>
        </Stack>
      </Card>
      {showAddDialog && (
        <PromptDialog
          title={<Trans id="New Template" />}
          onCancel={() => setShowAddDialog(false)}
          onSubmit={handleSubmit(onSubmit)}
          loading={false}
        >
          <FormControl>
            <FormLabel required>
              <Trans id="Name" />
            </FormLabel>
            <Input {...register('name')} required />
          </FormControl>

          <Checkbox {...register('isDefault')} label={<Trans id="Set as default" />} />
        </PromptDialog>
      )}
    </>
  );
};
